/* OSMODI stable enhancement layer. Loaded globally; duplicate executions are ignored. */
(function () {
  if (window.__osmodiPolishLoaded) return;
  window.__osmodiPolishLoaded = true;

  const desktop = window.matchMedia('(min-width: 1000px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const focusables = (root) => Array.from(root?.querySelectorAll(
    'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex]:not([tabindex="-1"])'
  ) || []).filter((node) => !node.hidden && node.offsetParent !== null && node.getAttribute('aria-hidden') !== 'true');

  const syncHeaderMetrics = () => {
    const growthHeader = document.querySelector('[data-v4-header]');
    if (growthHeader) {
      const growthHeight = Math.round(growthHeader.getBoundingClientRect().height) || (desktop.matches ? 106 : 91);
      document.documentElement.style.setProperty('--oc-header-h', `${growthHeight}px`);
      document.body.style.setProperty('--header-height', `${growthHeight}px`);
      document.body.style.setProperty('--header-group-height', `${growthHeight}px`);
      return;
    }
    const fixedBarHeight = desktop.matches ? 72 : 64;
    const announcement = document.querySelector('.oc-announce');
    const announcementHeight = announcement ? Math.round(announcement.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--oc-header-h', `${fixedBarHeight}px`);
    document.body.style.setProperty('--header-height', `${fixedBarHeight}px`);
    document.body.style.setProperty('--header-group-height', `${fixedBarHeight + announcementHeight}px`);
  };

  const trapFocus = (event, root) => {
    if (event.key !== 'Tab') return;
    const items = focusables(root);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const ensureToastRegion = () => {
    let region = document.querySelector('[data-oc-toast-region]');
    if (region) return region;
    region = document.createElement('div');
    region.className = 'oc-toast-region';
    region.dataset.ocToastRegion = '';
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    document.body.append(region);
    return region;
  };

  const announce = (message) => {
    const toast = document.createElement('div');
    toast.className = 'oc-toast';
    toast.textContent = message;
    ensureToastRegion().append(toast);
    requestAnimationFrame(() => { toast.dataset.visible = 'true'; });
    window.setTimeout(() => {
      delete toast.dataset.visible;
      window.setTimeout(() => toast.remove(), reduceMotion.matches ? 0 : 220);
    }, 2400);
  };

  const initHeader = (scope = document) => {
    const header = scope.querySelector?.('[data-oc-header]') || (scope.matches?.('[data-oc-header]') ? scope : null);
    if (!header || header.dataset.polishReady === 'true') return;
    header.dataset.polishReady = 'true';

    syncHeaderMetrics();

    const section = header.closest('.shopify-section') || document;
    const drawer = section.querySelector?.('[data-oc-drawer]') || document.querySelector('[data-oc-drawer]');
    const openButton = header.querySelector('[data-oc-drawer-open]');
    const drawerPanel = drawer?.querySelector('.oc-drawer__panel');
    let previousFocus = null;

    if (drawer && drawerPanel && openButton) {
      const syncDrawer = () => {
        const open = drawer.dataset.open === 'true';
        drawer.setAttribute('aria-hidden', String(!open));
        if (open) {
          previousFocus ||= document.activeElement;
          requestAnimationFrame(() => focusables(drawerPanel)[0]?.focus({ preventScroll: true }));
        } else if (previousFocus) {
          previousFocus.focus?.({ preventScroll: true });
          previousFocus = null;
        }
      };
      syncDrawer();
      new MutationObserver(syncDrawer).observe(drawer, { attributes: true, attributeFilter: ['data-open'] });
      drawer.addEventListener('keydown', (event) => {
        if (drawer.dataset.open === 'true') trapFocus(event, drawerPanel);
      });
    }

    const searchbar = header.querySelector('[data-oc-searchbar]');
    const searchToggle = header.querySelector('[data-oc-search-toggle]');
    if (searchbar && searchToggle) {
      const syncSearch = () => searchbar.setAttribute('aria-hidden', String(searchbar.dataset.open !== 'true'));
      syncSearch();
      new MutationObserver(syncSearch).observe(searchbar, { attributes: true, attributeFilter: ['data-open'] });
      document.addEventListener('click', (event) => {
        if (searchbar.dataset.open !== 'true' || header.contains(event.target)) return;
        searchbar.dataset.open = 'false';
        searchToggle.setAttribute('aria-expanded', 'false');
      });
    }

    header.querySelectorAll('[data-oc-nav-trigger]').forEach((trigger) => {
      trigger.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowDown') return;
        const panel = trigger.closest('[data-oc-nav-item]')?.querySelector('.oc-mega');
        const first = focusables(panel)[0];
        if (!first) return;
        event.preventDefault();
        if (trigger.getAttribute('aria-expanded') !== 'true') trigger.click();
        requestAnimationFrame(() => first.focus());
      });
    });
  };

  const initRails = (scope = document) => {
    scope.querySelectorAll?.('[data-oc-rail-track]').forEach((track) => {
      if (track.dataset.polishReady === 'true') return;
      track.dataset.polishReady = 'true';
      track.tabIndex = 0;
      track.setAttribute('role', 'region');
      track.setAttribute('aria-label', track.getAttribute('aria-label') || 'Scrollable fragrance products');
      track.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const amount = Math.max(track.clientWidth * 0.82, 240) * (event.key === 'ArrowLeft' ? -1 : 1);
        track.scrollBy({ left: amount, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      });
    });
  };

  const initQuickAdd = (scope = document) => {
    scope.querySelectorAll?.('[data-oc-quickadd]').forEach((button) => {
      if (button.dataset.polishFeedbackReady === 'true') return;
      button.dataset.polishFeedbackReady = 'true';
      button.addEventListener('click', () => button.setAttribute('aria-busy', 'true'), { capture: true });
      document.addEventListener('cart:updated', () => {
        if (button.getAttribute('aria-busy') !== 'true') return;
        const label = button.getAttribute('aria-label') || 'Fragrance';
        announce(`${label.replace(/^Add /i, '').replace(/ to cart$/i, '')} added to your bag.`);
        button.removeAttribute('aria-busy');
      });
    });
  };

  const initAll = (scope = document) => {
    syncHeaderMetrics();
    initHeader(scope);
    initRails(scope);
    initQuickAdd(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  window.addEventListener('resize', syncHeaderMetrics, { passive: true });
  desktop.addEventListener?.('change', syncHeaderMetrics);
  document.addEventListener('shopify:section:load', (event) => initAll(event.target || document));
})();
